import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { keyBy } from "lodash-es";
import { FormProvider, useForm } from "react-hook-form";

import { AgentPromptEditor } from "src/agentNode/AgentPromptEditor";
import { AgentSelectionEditor } from "src/agentNode/AgentSelectionEditor";
import { AgentEnvironmentConfig } from "src/agentNode/components/AgentEnvironmentConfig";
import { AgentInputMappings } from "src/agentNode/components/AgentInputMappings";
import { AgentResponseMapping } from "src/agentNode/components/AgentResponseMapping";
import { AgentSelectionModal } from "src/agentNode/components/agentSelectionModal/AgentSelectionModal";
import { NoAgentSelected } from "src/agentNode/components/agentSelectionModal/NoAgentSelected";
import { useAgentSchemas, useAgentUpdateKnownEtag } from "src/agentNode/hooks";
import { AgentNodeForm } from "src/agentNode/types";
import {
  backfillAgentNodeInsights,
  setupAgentNodeForm,
} from "src/agentNode/utils";
import { useAgent, useAgentVersion } from "src/api/agents/management";
import { FieldErrors } from "src/api/types";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import type { Agent, AgentVersion } from "src/clients/agent-management";
import { AgentNode, AgentNodeData } from "src/constants/NodeDataTypes";
import { Accordion } from "src/design-system/Accordion";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { useCanvasSearchHighlight } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  selectedNode: AgentNode;
} & NodeEditorBaseProps<AgentNodeData>;

const usePreviousValues = (nodeData: AgentNodeData) => {
  return useMemo(() => nodeData, [nodeData]);
};

// Helper to detect if a resource was deleted (403 after deletion or 404 not found)
const isResourceDeleted = (error: Error | null) => {
  if (!error) return false;
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status === 403 || status === 404;
  }
  return false;
};

// Error state components
type AgentDeletedStateProps = {
  immutable: boolean;
  onSelectAgent: () => void;
};

const AgentDeletedState: React.FC<AgentDeletedStateProps> = ({
  immutable,
  onSelectAgent,
}) => (
  <div className="space-y-3 p-5">
    <Callout variant="error">
      The selected Agent no longer exists. Please select a different agent.
    </Callout>
    {!immutable && (
      <Button
        dataLoc="select-different-agent"
        iconLeft={faEdit}
        size="sm"
        variant="secondary"
        onClick={onSelectAgent}
      >
        Select Agent
      </Button>
    )}
  </div>
);

type VersionDeletedStateProps = {
  agentName: string;
  immutable: boolean;
  onSelectAgent: () => void;
};

const VersionDeletedState: React.FC<VersionDeletedStateProps> = ({
  agentName,
  immutable,
  onSelectAgent,
}) => (
  <div className="space-y-3 p-5">
    <Callout variant="error">
      The selected version of "{agentName}" no longer exists. Please select a
      different version.
    </Callout>
    {!immutable && (
      <Button
        dataLoc="select-different-version"
        iconLeft={faEdit}
        size="sm"
        variant="secondary"
        onClick={onSelectAgent}
      >
        Select Version
      </Button>
    )}
  </div>
);

// Component to handle agent selection section with different states
type AgentSelectionSectionProps = {
  agentVersionStatus:
    | "no-selection"
    | "agent-deleted"
    | "version-deleted"
    | "loading"
    | "loaded";
  agentName: string;
  immutable: boolean;
  onRequestOpenModal: () => void;
};

const AgentSelectionSection: React.FC<AgentSelectionSectionProps> = ({
  agentVersionStatus,
  agentName,
  immutable,
  onRequestOpenModal,
}) => {
  if (agentVersionStatus === "agent-deleted") {
    return (
      <AgentDeletedState
        immutable={immutable}
        onSelectAgent={onRequestOpenModal}
      />
    );
  }

  if (agentVersionStatus === "version-deleted") {
    return (
      <VersionDeletedState
        agentName={agentName}
        immutable={immutable}
        onSelectAgent={onRequestOpenModal}
      />
    );
  }

  return (
    <AgentSelectionEditor
      immutable={immutable}
      onRequestOpenModal={onRequestOpenModal}
    />
  );
};

export const AgentNodeEditor: React.FC<Props> = ({
  selectedNode,
  immutable,
  isReactive,
  displayedError,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const { highlightedMatch } = useCanvasSearchHighlight();

  const nodeData = usePreviousValues(selectedNode.data);
  const nodeId = selectedNode.id;
  const isHighlightedNode = highlightedMatch?.nodeId === nodeId;

  const [accordionValue, setAccordionValue] = useState<string[]>(["agent"]);
  const effectiveAccordionValue =
    renderedInDiffView || isHighlightedNode
      ? ["agent", "mappings", "advanced"]
      : accordionValue;

  const fieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  // Fetch agent and version data
  const agentQuery = useAgent(nodeData.agent_id ?? "");
  const versionQuery = useAgentVersion(
    nodeData.agent_id ?? "",
    nodeData.agent_version_id ?? "",
  );
  const agentSchemas = useAgentSchemas(versionQuery.data);

  const hasAgentSelected = Boolean(
    nodeData.agent_id && nodeData.agent_version_id,
  );

  // Determine agent/version status
  const agentVersionStatus = useMemo(() => {
    // Only check for errors if an agent is actually selected
    if (!hasAgentSelected) return "no-selection" as const;

    if (agentQuery.error && isResourceDeleted(agentQuery.error))
      return "agent-deleted" as const;
    if (versionQuery.error && isResourceDeleted(versionQuery.error))
      return "version-deleted" as const;
    if (agentQuery.isLoading || versionQuery.isLoading)
      return "loading" as const;

    return "loaded" as const;
  }, [
    hasAgentSelected,
    agentQuery.error,
    agentQuery.isLoading,
    versionQuery.error,
    versionQuery.isLoading,
  ]);

  // Agent selection modal state
  const [agentModalOpen, setAgentModalOpen] = useState(false);

  // Stable callback to open modal
  const handleOpenModal = useCallback(() => {
    setAgentModalOpen(true);
  }, []);

  // Keep agent_version_known_etag in sync with the actual agent version etag
  useAgentUpdateKnownEtag({
    nodeId,
    nodeData,
    immutable,
    isReactive,
    onUpdate,
    etag: versionQuery.data?.etag,
  });

  // Setup form with schema-aware mappings (similar to LoopNodeEditor)
  const [defaultValues, schemaMappingErrors, fieldsToRemove] = useMemo(() => {
    // Only setup the form once the agent version has been fetched
    if (immutable || versionQuery.data === undefined) {
      return [backfillAgentNodeInsights(nodeData), [], []];
    }
    return setupAgentNodeForm(nodeData, agentSchemas);
  }, [immutable, versionQuery.data, nodeData, agentSchemas]);

  // Filter errors by type for input mappings
  const inputMappingErrors = keyBy(
    schemaMappingErrors.filter(
      (e) => e.type === "input" && e.issue === "field-missing",
    ),
    "fieldName",
  );

  const formMethods = useForm<AgentNodeForm>({
    defaultValues,
    ...(isReactive && { values: defaultValues }),
  });

  const handleSubmitRef = useRef<VoidFunction>(() => {});

  // Keep ref up to date with current submit handler
  handleSubmitRef.current = formMethods.handleSubmit((data) => {
    onUpdate({
      newData: {
        ...nodeData,
        ...data,
      },
    });
  });

  // Handle field removal when schema changes (similar to LoopNodeEditor)
  const { unregister } = formMethods;
  useEffect(() => {
    // Prevent unnecessary submits because they e.g. result in change history entries
    if (fieldsToRemove.length > 0) {
      unregister(fieldsToRemove);
      if (!isReactive && !immutable) {
        handleSubmitRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsToRemove.join(","), unregister, isReactive, immutable]);

  useFormApplyNodeSuggestion<AgentNodeData>(nodeId, (suggestion) => {
    if (suggestion.prompt) {
      formMethods.setValue("prompt", suggestion.prompt);
    }
  });

  const handleSelectAgent = (version: AgentVersion, agent: Agent) => {
    // When a flow version is selected we want to immediately update the node
    const newData = {
      ...nodeData,
      ...formMethods.getValues(),
      agent_id: agent.id,
      agent_version_known_etag: version.etag,
      agent_version_id: version.id,
    };
    onUpdate({
      newData,
    });
  };

  useSubmitForm({
    onChange: (data: AgentNodeForm) => {
      onUpdate({ newData: { ...nodeData, ...data } });
    },
    disabled: isReactive || immutable,
    previousValues: defaultValues,
    subscribe: formMethods.subscribe,
  });

  const isAgentDeleted =
    agentVersionStatus === "agent-deleted" ||
    agentVersionStatus === "version-deleted";

  return (
    <FormProvider {...formMethods}>
      <Accordion value={effectiveAccordionValue} onChange={setAccordionValue}>
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Select the Agent you want to call"
          value="agent"
        >
          <AgentSelectionSection
            agentName={agentQuery.data?.name ?? "Agent"}
            agentVersionStatus={agentVersionStatus}
            immutable={immutable}
            onRequestOpenModal={handleOpenModal}
          />
        </Accordion.Item>

        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Map inputs and outputs of the Agent"
          value="mappings"
        >
          <div className="space-y-4">
            {hasAgentSelected && !isAgentDeleted ? (
              <>
                <AgentInputMappings
                  agentId={nodeData.agent_id ?? ""}
                  agentName={agentQuery.data?.name ?? "Agent"}
                  immutable={immutable}
                  inputMappingErrors={inputMappingErrors}
                  inputSchema={agentSchemas.input}
                  isLoading={versionQuery.isLoading}
                  runFieldErrors={fieldErrors}
                  versionId={nodeData.agent_version_id ?? ""}
                />
                <AgentPromptEditor
                  immutable={immutable}
                  isReactive={isReactive}
                  nodeId={nodeId}
                />
                <AgentResponseMapping
                  immutable={immutable}
                  isOutputSchemaLoading={versionQuery.isLoading}
                  outputSchema={versionQuery.data?.configuration.output_schema}
                />
              </>
            ) : (
              <NoAgentSelected
                handleSelectAgent={handleOpenModal}
                immutable={immutable}
                type="input-mapping"
              />
            )}
          </div>
        </Accordion.Item>

        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Advanced settings"
          value="advanced"
        >
          <AgentEnvironmentConfig disabled={immutable} />
        </Accordion.Item>
      </Accordion>

      <AgentSelectionModal
        handleClose={() => setAgentModalOpen(false)}
        handleSubmit={handleSelectAgent}
        open={agentModalOpen}
      />
    </FormProvider>
  );
};
